import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Animated } from 'react-native';
import { titlesWithClosedSidebar } from '@/components/ui/therapist/AnimatedHeader/index';

interface SidebarAnimationContextValue {
  appTitle: string;
  bigNavHeight: number;
  isOpen: boolean;
  headerHeight: number;
  setAppTitle: (title: string) => void;
  setHeaderHeight: (height: number) => void;
  setShowHeader: (showHeader: boolean) => void;
  showHeader: boolean;
  sidebarWidthRef: Animated.Value;
  sidebarWidthValue: number;
  smallNavHeight: number;
  toggleSidebar: () => void;
}

const SidebarAnimationContext =
  createContext<SidebarAnimationContextValue | null>(null);

export const useSidebarAnimation = () => {
  const ctx = useContext(SidebarAnimationContext);
  if (!ctx)
    throw new Error(
      'useSidebarAnimation must be used within SidebarAnimationProvider',
    );
  return ctx;
};

const bigNavHeight = 88;
const smallNavHeight = 55;

export const SidebarAnimationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [appTitle, setAppTitle] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [headerHeight, setHeaderHeight] = useState<number>(
    titlesWithClosedSidebar.includes(appTitle) ? smallNavHeight : bigNavHeight,
  );
  const [sidebarWidthValue, setSidebarWidthValue] = useState<number>(250);
  const [showHeader, setShowHeader] = useState<boolean>(true);
  const sidebarWidthRef = useRef(new Animated.Value(250)).current;

  const toggleSidebar = () => {
    Animated.timing(sidebarWidthRef, {
      toValue: isOpen ? 60 : 250,
      duration: 350,
      useNativeDriver: false,
    }).start();

    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const id = sidebarWidthRef.addListener(({ value }) => {
      setSidebarWidthValue(value);
    });
    return () => {
      sidebarWidthRef.removeListener(id);
    };
  }, [sidebarWidthRef]);

  return (
    <SidebarAnimationContext.Provider
      value={{
        appTitle,
        bigNavHeight,
        isOpen,
        headerHeight,
        setAppTitle,
        setHeaderHeight,
        setShowHeader,
        showHeader,
        sidebarWidthRef,
        sidebarWidthValue,
        smallNavHeight,
        toggleSidebar,
      }}
    >
      {children}
    </SidebarAnimationContext.Provider>
  );
};
